/**
 * Cloned from components/tableView/TableView.tsx.
 * This component is built for those using the `listTickets` endpoint,
 *  whose API response schema differs from QB's generic schema.
 */
import React, { useEffect, useMemo } from 'react';

import { css } from '@emotion/react';
import { colors, HStack } from '@rtkwlf/fenrir-react';
import type { ResponseError, Ticket } from '@rtkwlf/rendall';
import type { QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ColumnInstance } from 'react-table';
import { StringParam, useQueryParams } from 'use-query-params';

import { AuthError } from '@packages/components/error/AuthError';
import { Error } from '@packages/components/error/Error';
import { GeneralWrapper } from '@packages/components/generalWrapper/GeneralWrapper';
import type { HeaderType } from '@packages/components/header/Header';
import { Header } from '@packages/components/header/Header';
import type { SortFn } from '@packages/components/table/Table';
import { Table as UITable } from '@packages/components/table/Table';
import { useSorting } from '@packages/hooks/use-sorting/use-sorting';

import type { TicketFiltersObject } from '../ticketList/filter/ticketFilters.constants';
import { filterTickets } from '../ticketList/filter/ticketFilters.constants';
import { TicketDetailsReadingPane } from '../ticketSummary/ticketDetails/ticketDetailsReadingPane/TicketDetailsReadingPane';

export type TicketTableProps = {
  columns: ColumnInstance[];
  responseError?: ResponseError | null;
  header?: HeaderType;
  filters?: TicketFiltersObject;
  maxRowsPerPage?: number; // To show All Rows use 0
  showPagination?: boolean;
  isLoading: boolean;
  isError: boolean;
  fetched: Required<Ticket>[];
  noData?: React.ReactElement;
  readingPane?: boolean;
  setFilteredData?: (data: Required<Ticket>[]) => void;
  ticketListQueryKey?: QueryKey;
};

export const TicketTable: React.FC<TicketTableProps> = ({
  columns,
  header,
  filters,
  maxRowsPerPage,
  showPagination,
  isLoading,
  isError,
  responseError,
  fetched,
  noData,
  readingPane = false,
  setFilteredData,
  ticketListQueryKey,
}) => {
  const { t } = useTranslation();
  const { sorting, setSorting } = useSorting();

  const [query, setQuery] = useQueryParams({ ticketId: StringParam });

  const queryClient = useQueryClient();

  const data = useMemo(() => {
    if (ticketListQueryKey) {
      queryClient.getQueryData(ticketListQueryKey);
    }
    if (filters) {
      return filterTickets(fetched, filters);
    }
    return fetched;
  }, [fetched, filters, queryClient, ticketListQueryKey]);

  const activeTicket = useMemo(
    () => data.find(({ id }) => id.toString() === query.ticketId),
    [data, query.ticketId],
  );

  const rowsPerPage = useMemo(
    () => (maxRowsPerPage === 0 ? data.length : maxRowsPerPage),
    [data, maxRowsPerPage],
  );

  const handleSetActiveTicket = (rowObject: Ticket) => {
    if (readingPane) {
      setQuery({ ticketId: rowObject.id.toString() });
    }
  };

  const error =
    (responseError?.response as Response | undefined)?.status === 403 ? (
      <AuthError />
    ) : (
      <Error />
    );

  useEffect(() => {
    if (setFilteredData) {
      setFilteredData(data);
    }
  }, [setFilteredData, data]);

  return (
    <>
      {header && <Header details={header} isLoading={isLoading} />}
      <HStack
        paddingTop="zero"
        paddingLeft="large"
        paddingRight="large"
        yAlign="top"
      >
        <GeneralWrapper
          // Note - Following isError condition is added for https://arcticwolf.atlassian.net/browse/AWN-126406 ticket.
          isError={data.length > 0 && isError ? false : isError}
          isLoading={isLoading}
          isNoData={data.length === 0}
          noDataMessage={t('dataExplorer:associatedAlerts.noDataMessage')}
          noData={noData}
          overflow="auto"
          error={error}
          css={css({
            flex: 1,
          })}
        >
          <UITable
            manualSortBy={false}
            disableMultiSort
            manualPagination={false}
            style={css({
              ...(readingPane
                ? {
                    maxHeight: '75vh',
                    'thead tr th': {
                      background: `rgb(${colors.background.widget.default})`,
                      position: 'sticky',
                      top: '0',
                      zIndex: 1,
                    },
                  }
                : {}),
              'tbody tr': {
                ':hover': {
                  color: `rgb(${colors.text.default.body})`,
                },
                color: `rgb(${colors.text.default.body})`,
                cursor: 'default',
              },
            })}
            data={data}
            initialSortBy={sorting}
            onSort={setSorting as SortFn}
            columns={columns}
            rowsPerPage={rowsPerPage}
            totalRows={data.length}
            showPagination={!isLoading && showPagination}
            onRowClick={handleSetActiveTicket as (ticket: unknown) => void}
            activeRowData={activeTicket}
          />
        </GeneralWrapper>
        {activeTicket && (
          <TicketDetailsReadingPane
            key={activeTicket.id}
            ticket={activeTicket}
            close={() => setQuery({ ticketId: undefined })}
            ticketListQueryKey={ticketListQueryKey}
          />
        )}
      </HStack>
    </>
  );
};
